import type { RouterOutputs } from "@api/trpc/routers/_app";
import {
  BookmarkFilledIcon,
  Pencil1Icon,
  Share2Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { FolderPlus, Loader2, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRemoveFromFolder } from "@/features/folders/api/use-remove-from-folder";
import useAddToFolderModal from "@/features/folders/hooks/use-add-to-folder-modal";
import { useDeleteStudyset } from "@/features/studysets/api/use-delete-studyset";
import { useToggleSaveStudySet } from "@/features/studysets/api/use-toggle-save-studyset";
import useShareStudySetModal from "@/features/studysets/hooks/use-share-studyset-modal";
import { cn, getSlug } from "@/lib/utils";
import { useI18n } from "@/locales/client";

interface StudySetDropDownProps {
  studySet: RouterOutputs["studysets"]["getPublic"]["results"][0];
  setIsDeleteOpen: Dispatch<SetStateAction<boolean>>;
  trigger?: ReactNode;
  folderId?: string; // Add this prop
}

const StudySetDropDown = ({
  studySet,
  setIsDeleteOpen,
  trigger,
  folderId,
}: StudySetDropDownProps) => {
  const studySetId = studySet.id;
  const t = useI18n();
  const deleteMutation = useDeleteStudyset({ id: studySetId });
  const toggleSavedStudySet = useToggleSaveStudySet({ id: studySet.id });
  const { onOpen } = useShareStudySetModal();
  const publicUrl = `/studysets/${getSlug(studySet.name, studySetId)}`;
  const { onOpen: openMoveFolderModal } = useAddToFolderModal();
  const _removeFromFolder = useRemoveFromFolder();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreHorizontal className="size-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-40" align="start">
        {/* Add this menu item before Share */}
        <DropdownMenuItem
          className="flex gap-2 p-[10px]"
          onClick={(e) => {
            e.stopPropagation();
            onOpen({
              publicUrl: publicUrl,
              isPublic: studySet.isPublic,
              studySetId,
              type: studySet.type,
              isAuthenticated: true,
            });
          }}
        >
          <Share2Icon className="size-4" />
          <span className="text-sm">{t("share")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex gap-2 p-[10px]"
          onClick={(e) => {
            e.stopPropagation();
            openMoveFolderModal({ studySetId: studySetId });
          }}
        >
          <FolderPlus className="size-4" />
          <span className="text-sm">{t("manageFolders")}</span>
        </DropdownMenuItem>
        {studySet.type === "personal" ? (
          <>
            <DropdownMenuItem asChild className="p-[10px] flex gap-2">
              <Link href={`/studysets/${studySetId}/edit`}>
                <Pencil1Icon className="size-4" />
                <span className="text-sm">{t("edit")}</span>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="text-destructive focus:text-destructive flex gap-2 p-[10px]"
              disabled={deleteMutation.isPending}
              onClick={async (e) => {
                e.stopPropagation();
                setIsDeleteOpen(true);
              }}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <TrashIcon className="size-4" />
              )}
              <span className="text-sm">{t("delete")}</span>
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem
            className={cn(
              "flex gap-2 p-[10px]",
              studySet.isLiked && "text-destructive focus:text-destructive",
            )}
            disabled={toggleSavedStudySet.isPending}
            onClick={(e) => {
              e.stopPropagation();
              if (!studySet.isLiked) {
                toggleSavedStudySet.mutate({
                  id: studySet.id,
                  isLiked: studySet.isLiked,
                });
              } else {
                setIsDeleteOpen(true);
              }
            }}
          >
            {studySet.isLiked ? (
              <>
                {toggleSavedStudySet.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <BookmarkFilledIcon className="size-4" />
                )}
                <span className="text-sm">{t("unsave")}</span>
              </>
            ) : (
              <>
                {toggleSavedStudySet.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <BookmarkFilledIcon className="size-4" />
                )}
                <span className="text-sm">{t("save")}</span>
              </>
            )}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StudySetDropDown;
